import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import fetchAllPages from "../../utils/fetchAllPages";
import { buildFilterParams, FilterTarget } from "../../utils/utils";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const organizationAPI = createApi({
  reducerPath: "organizationAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetOrganizations",
    "tagGetOrganizationsLight",
    "tagGetOrganizationByOrganization",
    "tagGetOrganizationsAndChilds",
    "tagPostOrganization",
    "tagPutOrganization",
    "tagGetHolidays",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ORGANIZATIONS */
    getOrganizations: builder.query({
      query: (body: any) => {
        // See buildFilterParams (src/utils/utils.ts) for the LiftCloud wire
        // format and comma-safety details. parent_id always leads so it's
        // always the base condition (position 0) that every other active
        // column filter ANDs against, scoping the list to the org tree.
        const singles: FilterTarget[] = [
          { target: "parent_id", value: body.parentId },
          { target: "name", value: body.filterName },
          { target: "address", value: body.filterAddress },
          { target: "country", value: body.filterCountry },
          { target: "state", value: body.filterState },
          { target: "city", value: body.filterCity },
          { target: "phone", value: body.filterPhone },
          { target: "type", value: body.filterOrgType },
        ];
        let url = `/orgs/?&page=${body.page}&limit=${body.limit}${buildFilterParams(singles)}`;
        if (body.statusFilter) {
          url += `&status=${body.statusFilter}`;
        }
        const sortField = body.sortField || "name";
        // LIFT-965 ship-with: the server corrected ?sort_direction
        // (asc -> ASC, desc -> DESC); the old build inverted it, so send
        // the flip to keep every table's current visual order.
        const requested = body.sortDirection || "desc";
        const sortDirection = requested === "asc" ? "desc" : "asc";
        url += `&sort=${sortField}&sort_direction=${sortDirection}`;
        return {
          url,
          headers: { authorization: body.token },
        };
      },
      providesTags: ["tagGetOrganizations"],
    }),
    //* *********************************************************** */
    //* GET ALL ORGANIZATIONS LIGHT */
    getOrganizationsLight: builder.query({
      query: (body: any) => ({
        url: `/orgs/light/${body?.withBanks ? "?with_banks=true" : ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOrganizationsLight"],
    }),
    //* *********************************************************** */
    //* GET ORGANIZATIONS BY ORGANIZATION */
    getOrganizationsByOrganization: builder.query({
      query: (body: any) => ({
        url: `/orgs/?filter_target=parent_id&filter=${body.orgId}&page=${body.page}&limit=${body.limit}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOrganizationByOrganization"],
    }),
    //* *********************************************************** */
    //* GET SPECIFIC ORGANIZATION AND ITS CHILDRENS */
    getOrganizationAndChilds: builder.query({
      query: (body: any) => ({
        url: `/orgs/${body.orgId}`,
        // url: `/orgs/${body.orgId}&filter_target=name&filter=hello`,
        // url: `/orgs/${body.orgId}/?&page=${body.page}&limit=${body.limit}${body.searchAndFilterString || ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOrganizationsAndChilds"],
    }),
    //* *********************************************************** */
    //* CREATE AN ORGANIZATION */
    postOrganization: builder.mutation({
      query: (body: any) => ({
        url: "/orgs/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateOrganization" },
      invalidatesTags: [
        "tagGetOrganizations",
        "tagGetOrganizationByOrganization",
        "tagGetOrganizationsAndChilds",
      ],
    }),
    //* *********************************************************** */
    //* EDIT AN ORGANIZATION */
    putOrganization: builder.mutation({
      query: (body: any) => ({
        url: `/orgs/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateOrganization" },
      invalidatesTags: [
        "tagGetOrganizations",
        "tagGetOrganizationByOrganization",
        "tagGetOrganizationsAndChilds",
      ],
    }),
    //* *********************************************************** */
    //* DELETE AN ORGANIZATION */
    deleteOrganization: builder.mutation({
      query: (body: any) => ({
        url: `/orgs/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: [
        "tagGetOrganizations",
        "tagGetOrganizationByOrganization",
        "tagGetOrganizationsAndChilds",
      ],
    }),
    //* *********************************************************** */
    //* PATCH ORGANIZATION SETTINGS */
    patchOrganizationSettings: builder.mutation({
      query: (body: any) => ({
        url: `/orgs/${body.id}/settings`,
        method: "PATCH",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetOrganizations", "tagGetOrganizationsAndChilds"],
    }),
    //* *********************************************************** */
    //* EXPORT FULL ORGANIZATION */
    getExportFullOrganization: builder.query({
      query: (body: any) => ({
        url: `/export/full-org/${body.organizationId}?format=bin`,
        headers: { authorization: body.token },
        responseHandler: async (response) => {
          if (!response.ok) throw new Error("Error retrieving the file.");
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          return { blob, contentDisposition };
        },
      }),
      providesTags: ["tagGetOrganizations"],
    }),
    //* *********************************************************** */
    //* GET ALL HOLIDAYS */
    getHolidays: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/holidays/?filter_target=organization_id&filter=${body.orgId}&sort=month&sort_direction=asc&page=1&limit=100`,
          { authorization: body.token },
          100,
        ),
      providesTags: ["tagGetHolidays"],
    }),
    //* *********************************************************** */
    //* CREATE AN HOLIDAY */
    postHoliday: builder.mutation({
      query: (body: any) => ({
        url: "/holidays/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetHolidays"],
    }),
    //* *********************************************************** */
    //* EDIT AN HOLIDAY */
    putHoliday: builder.mutation({
      query: (body: any) => ({
        url: `/holidays/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetHolidays"],
    }),
    //* *********************************************************** */
    //* DELETE AN HOLIDAY */
    deleteHoliday: builder.mutation({
      query: (body: any) => ({
        url: `/holidays/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetHolidays"],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetOrganizationsQuery,
  useGetOrganizationsLightQuery,
  useLazyGetOrganizationsByOrganizationQuery,
  useLazyGetOrganizationAndChildsQuery,
  usePostOrganizationMutation,
  usePutOrganizationMutation,
  useDeleteOrganizationMutation,
  useLazyGetExportFullOrganizationQuery,
  useLazyGetHolidaysQuery,
  usePostHolidayMutation,
  usePutHolidayMutation,
  useDeleteHolidayMutation,
  usePatchOrganizationSettingsMutation,
} = organizationAPI;
